import {
    emptyAiStreamingUiState,
    intentSearchName,
    useAgentSelectorContext,
    useAiAgentAndPipelineResults,
    useAiStreamingUiState
} from "./react.intent";
import { useSelectedDataView } from "@me8eon/data_view_filter";
import { useMessages } from "@me8eon/sessions_messages";
import { BaseMessage, defaultAction, Message } from "@me8eon/messages";
import {
    ContextAndMessagesAndSelectedAndCard,
    executePipeline,
    PipelineDetailsData,
    selectAgent
} from "@me8eon/agent";
import { ErrorsOr, isErrors, isValue, LogAnd } from "@me8eon/errors";
import { useLoadingFlag, useQuestionSuggestions } from "@me8eon/async";
import { mapErrorToMessage } from "./utils";
import { useTranslation } from "@me8eon/translation";
import { useAgentContext } from "./context/AgentContext";
import { useJwtToken, useUserData } from "@me8eon/react_authentication";
import { AiStreamEvent } from "@me8eon/aiclient";
import { useMemo } from "react";

export function useDoAi() {
    const intent = useAgentContext();
    const [, setAgentAndPipelineResults] = useAiAgentAndPipelineResults();
    const [, setStreamingUiState] = useAiStreamingUiState();
    const [selected] = useAgentSelectorContext();

    const dataView = useSelectedDataView<"aiAssistant", any>();
    const [messages, setMessages] = useMessages();
    const [isLoading, setIsLoading] = useLoadingFlag();
    const [, setQs] = useQuestionSuggestions();
    const t = useTranslation();
    const { featureFlagEnabled } = useUserData();
    const jwtTokenFn = useJwtToken();

    const effectiveIntent = useMemo(() => {
        if (featureFlagEnabled || !intent.cards.cards) {
            return intent;
        }

        const { nautilus: _nautilus, sharepoint: _sharepoint, ...rest } = intent.cards.cards;

        return {
            ...intent,
            cards: {
                ...intent.cards,
                cards: rest
            }
        };
    }, [featureFlagEnabled, intent]);

    function resetStreamingState() {
        setStreamingUiState(emptyAiStreamingUiState);
    }

    function beginStreamingState(agentName?: string) {
        setStreamingUiState({
            status: "waiting-first-token",
            agentName,
            content: "",
            hasFirstChunk: false,
            error: undefined
        });
    }

    function applyStreamingEvent(event: AiStreamEvent, agentName?: string) {
        setStreamingUiState(current => {
            if (event.type === "text-start") {
                return {
                    status: "waiting-first-token",
                    agentName: agentName || current.agentName,
                    content: current.content,
                    hasFirstChunk: current.hasFirstChunk,
                    error: undefined
                };
            }

            if (event.type === "text-delta") {
                return {
                    status: "streaming",
                    agentName: agentName || current.agentName,
                    content: event.content,
                    hasFirstChunk: true,
                    error: undefined
                };
            }

            if (event.type === "text-end") {
                return {
                    status: "streaming",
                    agentName: agentName || current.agentName,
                    content: event.content,
                    hasFirstChunk: event.content.length > 0,
                    error: undefined
                };
            }

            if (event.type === "error") {
                return {
                    status: "idle",
                    agentName: agentName || current.agentName,
                    content: "",
                    hasFirstChunk: false,
                    error: event.error
                };
            }

            return current;
        });
    }

    function buildUnexpectedErrorMessage(agentName?: string): BaseMessage {
        return {
            role: "assistant",
            content: "I'm sorry I had an unexpected error processing your request",
            agent: agentName,
            action: defaultAction
        };
    }

    return async function (query: string) {
        if (!query) return;
        if (isLoading) return;

        const stDetails = dataView.search[intentSearchName];
        if (!stDetails) return;

        setIsLoading(true);
        setQs([]);
        setAgentAndPipelineResults({ loading: true });

        const provisionalAgentName = selected?.name || "assistant";

        resetStreamingState();
        beginStreamingState(provisionalAgentName);

        const userMessage: BaseMessage = {
            role: "user",
            content: query,
            action: defaultAction
        };

        const baseMessages: Message[] = [...messages];
        const messagesWithUser: Message[] = [...baseMessages, userMessage];

        try {
            if (!selected) {
                setMessages(messagesWithUser);

                const selectAgentResult: LogAnd<ErrorsOr<ContextAndMessagesAndSelectedAndCard<any>>> = await selectAgent(
                    effectiveIntent,
                    {
                        query,
                        lastSelected: "confused"
                    },
                    baseMessages
                );

                setAgentAndPipelineResults({ query, agentResult: selectAgentResult });

                if (isErrors(selectAgentResult)) {
                    resetStreamingState();

                    const errorMsg = mapErrorToMessage(selectAgentResult.errors?.[0]);
                    const errorMessage: BaseMessage = {
                        role: "assistant",
                        content: errorMsg ? t(errorMsg) : "I'm sorry I had an unexpected error processing your request",
                        action: defaultAction
                    };

                    setMessages([...messagesWithUser, errorMessage]);
                    return;
                }

                const {
                    agentCard,
                    selected: selectedAgentName,
                    messages: fromSelectionMessages,
                    context
                } = selectAgentResult.value;

                beginStreamingState(selectedAgentName);

                const data: PipelineDetailsData<any> = {
                    context,
                    originalMessages: fromSelectionMessages,
                    appendMessages: [userMessage],
                    stream: {
                        sink: async (event: AiStreamEvent) => {
                            if (
                                event.type === "text-start" ||
                                event.type === "text-delta" ||
                                event.type === "text-end" ||
                                event.type === "error"
                            ) {
                                applyStreamingEvent(event, selectedAgentName);
                            }
                        }
                    }
                };

                const executePipelineResult = await executePipeline(
                    effectiveIntent.executors,
                    data,
                    agentCard.pipeline
                );

                if (isValue(executePipelineResult)) {
                    const { appendMessages = [], suggestions = [] } = executePipelineResult.value;

                    const finalMessages: Message[] = appendMessages.length > 0
                        ? [...baseMessages, ...appendMessages]
                        : [
                            ...baseMessages,
                            userMessage,
                            {
                                role: "assistant",
                                content: `Agent ${selectedAgentName} did not return any messages`,
                                agent: selectedAgentName,
                                action: defaultAction
                            }
                        ];

                    setMessages(finalMessages);
                    setQs(suggestions);
                } else {
                    setMessages([
                        ...baseMessages,
                        userMessage,
                        buildUnexpectedErrorMessage(selectedAgentName)
                    ]);
                }

                resetStreamingState();

                setAgentAndPipelineResults({
                    query,
                    agentResult: selectAgentResult,
                    pipeline: executePipelineResult
                });

                return;
            }

            setMessages(messagesWithUser);

            const selectedAgentName = selected.name;
            beginStreamingState(selectedAgentName);

            const data: PipelineDetailsData<any> = {
                context: { query, lastSelected: selected.name },
                originalMessages: baseMessages,
                appendMessages: [userMessage],
                stream: {
                    sink: async (event: AiStreamEvent) => {
                        if (
                            event.type === "text-start" ||
                            event.type === "text-delta" ||
                            event.type === "text-end" ||
                            event.type === "error"
                        ) {
                            applyStreamingEvent(event, selectedAgentName);
                        }
                    }
                }
            };

            const executePipelineResult = await executePipeline(
                effectiveIntent.executors,
                data,
                selected.pipeline
            );

            if (isValue(executePipelineResult)) {
                const { appendMessages = [], suggestions = [] } = executePipelineResult.value;

                const finalMessages: Message[] = appendMessages.length > 0
                    ? [...baseMessages, ...appendMessages]
                    : [
                        ...baseMessages,
                        userMessage,
                        {
                            role: "assistant",
                            content: `Agent ${selected.name} did not return any messages`,
                            agent: selected.name,
                            action: defaultAction
                        }
                    ];

                setMessages(finalMessages);
                setQs(suggestions);
            } else {
                setMessages([
                    ...baseMessages,
                    userMessage,
                    buildUnexpectedErrorMessage(selected.name)
                ]);
            }

            resetStreamingState();

            setAgentAndPipelineResults({
                query,
                pipeline: executePipelineResult,
                agentResult: {
                    value: {
                        agentCard: selected,
                        selected: selected.name,
                        context: { query, lastSelected: selected.name },
                        messages: baseMessages
                    },
                    log: {
                        whatHappened: "agent already selected",
                        params: { selected: selected.name },
                        severity: "info"
                    }
                }
            });
        } catch (e: any) {
            console.error(e);

            resetStreamingState();

            setAgentAndPipelineResults({
                query,
                errors: { errors: [e.message || e.toString()] }
            });

            const errorMessage: BaseMessage = {
                role: "assistant",
                content: "I'm sorry I had an unexpected error processing your request",
                action: defaultAction
            };

            setMessages([...messagesWithUser, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };
}